import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createGame, guess } from "./engine.js";
import type { HangmanGame } from "./types.js";
import ConfettiSplash from "../../lib/ConfettiSplash.js";

type Mode = "single" | "local" | "multiplayer";
type PlayerColor = "red" | "yellow" | "green" | "purple" | "orange" | "teal";

const COLOR_OPTIONS: PlayerColor[] = ["red", "yellow", "green", "purple", "orange", "teal"];

const CODE_WORDS = [
  "apple", "beach", "candy", "dance", "eagle", "flame", "grape", "honey",
  "ivory", "jelly", "koala", "lemon", "mango", "noble", "ocean", "pearl",
  "queen", "rain", "sugar", "tiger", "umbra", "vivid", "whale", "xenon",
  "yacht", "zebra", "bloom", "coral", "dawn", "ember", "frost", "glow",
  "haven", "iris", "jade", "kite", "lune", "mist", "nova", "opal",
];

function generateCode(): string {
  const pick = () => CODE_WORDS[Math.floor(Math.random() * CODE_WORDS.length)];
  return `${pick()}-${pick()}-${pick()}`;
}

const LETTERS = "abcdefghijklmnopqrstuvwxyz".split("");
const ALPHABET = LETTERS;

const HANGMAN_STAGES = [
  "  +---+\n  |   |\n      |\n      |\n      |\n      |\n=========",
  "  +---+\n  |   |\n  O   |\n      |\n      |\n      |\n=========",
  "  +---+\n  |   |\n  O   |\n  |   |\n      |\n      |\n=========",
  "  +---+\n  |   |\n  O   |\n /|   |\n      |\n      |\n=========",
  "  +---+\n  |   |\n  O   |\n /|\\  |\n      |\n      |\n=========",
  "  +---+\n  |   |\n  O   |\n /|\\  |\n /    |\n      |\n=========",
  "  +---+\n  |   |\n  O   |\n /|\\  |\n / \\  |\n      |\n=========",
  "  +---+\n  |   |\n  O   |\n /|\\  |\n / \\  |\n  ~   |\n=========",
  "  +---+\n  |   |\n  O   |\n /|\\  |\n / \\  |\n ~ ~  |\n=========",
  "  +---+\n  |   |\n  O   |\n /|\\  |\n / \\  |\n~ ~ ~ |\n=========",
  "  +---+\n  |   |\n  O   |\n /|\\  |\n / \\  |\n~ ~ ~ ~\n=========",
];

export default function HangmanPage({ hideTitle = false }: { hideTitle?: boolean }) {
  const [mode, setMode] = useState<Mode>("single");
  const [difficulty, setDifficulty] = useState<string>("medium");
  const [game, setGame] = useState<HangmanGame>(() => createGame("medium"));

  const [mpConnected, setMpConnected] = useState(false);
  const [mpPlayerId, setMpPlayerId] = useState<1 | 2 | null>(null);
  const [roomCode, setRoomCode] = useState("");
  const [mpToken, setMpToken] = useState("");
  const [opponentName, setOpponentName] = useState("");
  const [yourTurn, setYourTurn] = useState(false);
  const [mpError, setMpError] = useState("");
  const [joinCode, setJoinCode] = useState(generateCode);
  const [p1Name, setP1Name] = useState("Player 1");
  const [p1Color, setP1Color] = useState<PlayerColor>("red");

  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollingRef = useRef(false);
  const roomCodeRef = useRef(roomCode);
  const mpPlayerIdRef = useRef(mpPlayerId);
  const mpConnectedRef = useRef(mpConnected);

  roomCodeRef.current = roomCode;
  mpPlayerIdRef.current = mpPlayerId;
  mpConnectedRef.current = mpConnected;

  const status = useMemo(() => {
    if (game.winner === "guesser") return "You guessed the word!";
    if (game.winner === "picker") return "Game Over — the word was: " + game.word;
    return `Wrong: ${game.wrongGuesses}/${game.maxWrong}`;
  }, [game]);

  function handleGuess(letter: string) {
    if (game.winner) return;
    if (mode === "multiplayer") {
      apiPost("/api/hangman/guess", { code: roomCode, playerId: mpPlayerId, token: mpToken, letter });
      return;
    }
    setGame((prev) => guess(prev, letter));
  }

  function resetGame() {
    if (mode === "multiplayer") {
      apiPost("/api/hangman/reset", { code: roomCode, playerId: mpPlayerId, token: mpToken });
      return;
    }
    setGame(createGame(difficulty));
  }

  function handleModeChange(next: Mode) {
    if (mode === "multiplayer") {
      if (roomCode && mpToken) apiPost("/api/leave", { code: roomCode, playerId: mpPlayerId, token: mpToken });
      stopPolling();
    }
    setMode(next);
    setGame(createGame(difficulty));
  }

  // ---- multiplayer --------------------------------------------------------

  async function apiPost(path: string, body: object) {
    try {
      const res = await fetch(path, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      return await res.json();
    } catch { return { type: "error", message: "Network error." }; }
  }

  function handleServerEvent(event: any) {
    switch (event.type) {
      case "opponent-joined":
        setOpponentName(event.playerName); setMpConnected(true);
        setMessage(`Opponent ${event.playerName} joined! Press New Game to start.`); break;
      case "game-state":
        setGame(event.game); setYourTurn(event.yourTurn); break;
      case "opponent-left":
        setMpConnected(false); setRoomCode(""); setOpponentName(""); setMpPlayerId(null); setMpToken("");
        setMessage("Opponent left."); setGame(createGame(difficulty)); break;
      case "error": setMpError(event.message); break;
    }
  }

  const startPolling = useCallback(() => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    async function poll() {
      if (!pollingRef.current) return;
      const code = roomCodeRef.current, pid = mpPlayerIdRef.current;
      if (!code || !pid) { pollingRef.current = false; return; }
      try {
        const res = await fetch(`/api/poll?code=${encodeURIComponent(code)}&playerId=${pid}`);
        const data = await res.json();
        for (const event of data.events) { handleServerEvent(event); if (event.type === "opponent-left") { pollingRef.current = false; return; } }
      } catch { /* retry */ }
      if (pollingRef.current) pollRef.current = setTimeout(poll, 200);
    }
    poll();
  }, []);
  function stopPolling() { pollingRef.current = false; if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; } }
  useEffect(() => { return () => stopPolling(); }, []);

  const [message, setMessage] = useState("");

  async function handleCreateRoom() {
    const result = await apiPost("/api/create-room", { playerName: p1Name.trim() || "Player 1", playerColor: p1Color });
    if (result.type === "error") { setMpError(result.message); return; }
    roomCodeRef.current = result.code; mpPlayerIdRef.current = result.playerId;
    setRoomCode(result.code); setMpPlayerId(result.playerId); setMpToken(result.token);
    setMessage(`Room created! Share: ${result.code}`); startPolling();
  }

  async function handleJoinRoom() {
    if (!joinCode.trim()) { setMpError("Enter a room code."); return; }
    const result = await apiPost("/api/join-room", { code: joinCode.trim(), playerName: p1Name.trim() || "Player 2", playerColor: p1Color });
    if (result.type === "error") { setMpError(result.message); return; }
    roomCodeRef.current = joinCode.trim(); mpPlayerIdRef.current = result.playerId;
    setMpPlayerId(result.playerId); setMpToken(result.token); setRoomCode(joinCode.trim());
    setOpponentName(result.opponentName); setP1Color(result.opponentColor);
    setMpConnected(true); setMessage(`Connected with ${result.opponentName}. Press New Game to start!`);
    startPolling();
  }

  async function handleLeaveGame() {
    await apiPost("/api/leave", { code: roomCode, playerId: mpPlayerId, token: mpToken });
    stopPolling(); setMpConnected(false); setRoomCode(""); setOpponentName(""); setMpPlayerId(null); setMpToken("");
    setGame(createGame(difficulty)); setMessage("Left.");
  }

  return (
    <div className="game-shell">
      <ConfettiSplash active={game.winner === "guesser"} winnerName="You" />
      {!hideTitle && <h1 className="title">Hangman</h1>}

      <section className="control-panel">
        <div className="row two-col">
          <label>Mode <select value={mode} onChange={(e) => handleModeChange(e.target.value as Mode)}>
            <option value="single">Single Player</option>
            <option value="local">Two Players Local</option>
            <option value="multiplayer">Multiplayer Online</option>
          </select></label>
          <label>Level <select value={difficulty} onChange={(e) => { setDifficulty(e.target.value); setGame(createGame(e.target.value)); }}>
            <option value="easy">Easy (10 tries)</option>
            <option value="medium">Medium (8 tries)</option>
            <option value="hard">Hard (6 tries)</option>
          </select></label>
        </div>

        {mode === "multiplayer" && !mpConnected && (
          <div className="row two-col"><label>Your Name <input value={p1Name} onChange={(e) => setP1Name(e.target.value)} maxLength={20} /></label>
          <label>Your Color <select value={p1Color} onChange={(e) => setP1Color(e.target.value as PlayerColor)}>{COLOR_OPTIONS.map((c) => (<option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>))}</select></label></div>
        )}
        {mode === "multiplayer" && !mpConnected && !roomCode && (
          <div className="row two-col"><label>Room Code <input value={joinCode} onChange={(e) => setJoinCode(e.target.value)} maxLength={40} /></label></div>
        )}
        {mode === "multiplayer" && !mpConnected && !roomCode && (
          <div className="mp-actions" style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
            <button className="mp-btn" onClick={handleCreateRoom}>Create Room</button>
            <button className="mp-btn mp-btn-secondary" onClick={handleJoinRoom}>Join Room</button>
          </div>
        )}
        {mode === "multiplayer" && roomCode && !mpConnected && (
          <div style={{ textAlign: 'center' }}>
            <p className="mp-label">Share this code with a friend:</p><p className="mp-code">{roomCode}</p>
            <p className="mp-waiting">Waiting for opponent...</p>
            <button className="mp-cancel" onClick={handleLeaveGame}>Cancel</button>
          </div>
        )}
        {mode === "multiplayer" && mpConnected && (
          <div className="mp-connected-bar"><span className="mp-connected-info">Connected with <strong>{opponentName}</strong></span></div>
        )}

        <div className="actions">
          {(mode !== "multiplayer" || mpConnected) && <button onClick={resetGame}>New Game</button>}
          {mode === "multiplayer" && mpConnected && <button className="mp-leave" onClick={handleLeaveGame}>Leave Game</button>}
        </div>
        {mpError && <p className="subtle mp-error">{mpError}</p>}
      </section>

      <section className="board-panel">
        <p className="status">{status}</p>

        <div className="hangman-drawing">
          <pre>{HANGMAN_STAGES[game.wrongGuesses]}</pre>
        </div>

        <div className="hangman-word">
          {game.display.map((ch, i) => (
            <span key={i} className="hangman-letter">{ch !== "_" ? ch : ""}</span>
          ))}
        </div>


        <p className="subtle" style={{ textAlign: 'center' }}>
          Guessed: {game.guessed.length > 0 ? game.guessed.join(", ") : "(none)"}
        </p>

        {!game.winner && (
          <div className="hangman-letters">
            {ALPHABET.map((l) => (
              <button key={l} className="hangman-btn" disabled={game.guessed.includes(l) || game.winner !== null} onClick={() => handleGuess(l)}>
                {l.toUpperCase()}
              </button>
            ))}
          </div>
        )}

        {game.winner && (
          <div className="actions" style={{ justifyContent: 'center', marginTop: 12 }}>
            <button onClick={resetGame}>Play Again</button>
          </div>
        )}
      </section>
    </div>
  );
}
